<template>
  <analytics-sub-page
    title="Compare"
    icon="down-left-and-up-right-to-center"
    css-class="analytics-compare"
    default-label="Compare"
    :show-divisions-dropdown="false"
    :section-specs="sectionSpecs"
  >
    <divisions-metrics ref="divisionsRef" />
    <industry-comparison-section
      v-if="portalUpdatesJan2026Enabled"
      ref="industryRef"
      :metrics="benchmarkMetrics"
      :sector="sector"
      :page-visits="pageVisits"
      :loading="benchmarkLoading"
    />
  </analytics-sub-page>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import { getBankingInfo } from '@/Banking';
import AnalyticsSubPage from '@/components/AnalyticsSubPage.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import IndustryComparisonSection from '@/pages/analyticsBenchmark/components/IndustryComparisonSection.vue';
import DivisionsMetrics from '@/pages/analyticsCompare/components/DivisionsMetrics.vue';
import { TIME_SERIES } from '@/utils/chart';

const { portalUpdatesJan2026Enabled } = useFeatureFlags();

const divisionsRef = ref(null);
const industryRef = ref(null);
const benchmarkLoading = ref(true);
const sector = ref(null);
const benchmarkMetrics = ref([]);
const pageVisits = ref([]);

const PAGE_VISIT_PRODUCTS = [
  { id: 'track_users', label: 'Budget Views' },
  { id: 'save_users', label: `${getBankingInfo().save.label} Views` },
  { id: 'stream_users', label: 'Pay Views' },
  { id: 'hub_users', label: 'Hub Views' },
];

const fetchBenchmarks = async () => {
  try {
    const data = await ApiClient.getSectorBenchmarks();
    sector.value = data?.sector || null;
    benchmarkMetrics.value = data?.metrics || [];
  } catch (error) {
    console.error('Failed to fetch sector benchmarks:', error);
  }
};

const fetchPageVisits = async () => {
  try {
    const response = await ApiClient.getAppFeaturesMetrics(TIME_SERIES.MONTH_TO_MONTH, null, true);
    const results = response?.results;
    if (!response?.sector || !results?.length) {
      return;
    }

    const uniqueUsers = results.find(r => r.id === 'unique_users')?.calculated?.last_complete || 0;
    if (!uniqueUsers) {
      return;
    }

    pageVisits.value = PAGE_VISIT_PRODUCTS.flatMap(({ id, label }) => {
      const result = results.find(r => r.id === id);
      if (!result) {
        return [];
      }
      const lastValue = result.calculated?.last_complete || 0;
      const sectorAverages = result.sector_averages;
      return [
        {
          label,
          value: (lastValue / uniqueUsers) * 100,
          average: sectorAverages?.[sectorAverages.length - 1] ?? null,
        },
      ];
    });
  } catch (error) {
    console.error('Failed to fetch page visits:', error);
  }
};

onBeforeMount(async () => {
  if (!portalUpdatesJan2026Enabled.value) {
    return;
  }
  benchmarkLoading.value = true;
  await Promise.all([fetchBenchmarks(), fetchPageVisits()]);
  benchmarkLoading.value = false;
});

const sectionSpecs = [
  { key: 'compare', label: 'Division Comparison', ref: divisionsRef },
  { key: 'industry', label: 'Industry Comparison', ref: industryRef },
];
</script>
